/**
 * MyPages - "Moje strony / Oferty" (P0)
 * 
 * Lista kart: Hodowla, Mioty, Reproduktory, Psy
 * Akcje: Zobacz, Edytuj, Udostępnij, Uśpij/Wznów, Usuń, Promuj
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import * as routes from '@/routes/routeMap';
import { 
  Home, Dog, Baby, Eye, Edit3, Share2, 
  Pause, Play, Trash2, Zap, MoreVertical,
  ExternalLink, QrCode, Loader2, Plus,
  Check, X, AlertCircle
} from 'lucide-react';

// ==================== DESIGN TOKENS ====================
const THEME = {
  bg: 'bg-[#F8F6F3]',
  surface: 'bg-white',
  border: 'border-zinc-200',
  text: 'text-zinc-900',
  text2: 'text-zinc-500',
  accent: 'bg-[#14B8A6]',
  accentText: 'text-[#14B8A6]',
};

// Entity types config
const ENTITY_CONFIG = {
  kennel: { 
    label: 'Hodowla', 
    icon: Home, 
    color: 'bg-orange-100 text-orange-700',
    viewRoute: (slug) => `/hodowla/${slug}`,
    editRoute: routes.hodowlaEdit
  },
  litter: { 
    label: 'Miot', 
    icon: Baby, 
    color: 'bg-amber-100 text-amber-700',
    viewRoute: (slug) => `/miot/${slug}`,
    editRoute: routes.miotEdit
  },
  stud: { 
    label: 'Reproduktor', 
    icon: Dog, 
    color: 'bg-blue-100 text-blue-700',
    viewRoute: (slug) => `/reproduktor/${slug}`,
    editRoute: routes.reproduktorEdit
  },
  dog: { 
    label: 'Pies', 
    icon: Dog, 
    color: 'bg-green-100 text-green-700',
    viewRoute: (slug) => `/pies/${slug}`,
    editRoute: routes.psyEdit
  }
};

// Status config
const STATUS_CONFIG = {
  active: { label: 'Aktywna', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  draft: { label: 'Szkic', color: 'bg-zinc-100 text-zinc-600', dot: 'bg-gray-400' },
  paused: { label: 'Uśpiona', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  archived: { label: 'Archiwalna', color: 'bg-red-100 text-red-600', dot: 'bg-red-400' }
};

export default function MyPages() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entities, setEntities] = useState([]);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [shareModalEntity, setShareModalEntity] = useState(null);
  const [deleteConfirmEntity, setDeleteConfirmEntity] = useState(null);

  useEffect(() => {
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch all entity types in parallel (using correct API endpoints)
      const [kennelsRes, littersRes, studsRes, dogsRes] = await Promise.all([
        axios.get(`${API}/kennels`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/litters`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/studs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/dogs`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      // Normalize entities
      const normalized = [
        ...(kennelsRes.data || []).map(k => ({
          ...k,
          type: 'kennel',
          status: k.is_paused ? 'paused' : (k.is_published !== false ? 'active' : 'draft'),
          image: k.gallery?.[0] || k.logo || null,
          name: k.name || 'Hodowla bez nazwy'
        })),
        ...(littersRes.data || []).map(l => ({
          ...l,
          type: 'litter',
          status: l.is_paused ? 'paused' : (l.is_published !== false ? 'active' : 'draft'),
          image: l.gallery?.[0] || null,
          name: l.name || `Miot ${l.breed || ''}`
        })),
        ...(studsRes.data || []).map(s => ({
          ...s,
          type: 'stud',
          status: s.is_paused ? 'paused' : (s.is_published !== false ? 'active' : 'draft'),
          image: s.gallery?.[0] || s.photos?.[0] || null,
          name: s.name || 'Reproduktor'
        })),
        ...(dogsRes.data || []).map(d => ({
          ...d,
          type: 'dog',
          status: d.is_paused ? 'paused' : 'active',
          image: d.gallery?.[0] || d.photos?.[0] || null,
          name: d.name || 'Pies'
        }))
      ];
      
      // Sort by created_at desc
      normalized.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      
      setEntities(normalized);
    } catch (err) {
      console.error('Fetch entities error:', err);
      toast.error(t('myPages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleView = (entity) => {
    const config = ENTITY_CONFIG[entity.type];
    if (config && entity.slug) {
      window.open(config.viewRoute(entity.slug), '_blank');
    }
  };

  const handleEdit = (entity) => {
    const config = ENTITY_CONFIG[entity.type];
    if (config?.editRoute) {
      navigate(config.editRoute(entity.id));
    }
    setActionMenuId(null);
  };

  const handleTogglePause = async (entity) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = entity.type === 'kennel' 
        ? `${API}/kennels/${entity.id}/toggle-pause`
        : `${API}/${entity.type}s/${entity.id}/toggle-pause`;
      
      await axios.patch(endpoint, {}, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success(entity.status === 'paused' ? t('myPages.resumed') : t('myPages.paused'));
      fetchEntities();
    } catch (err) {
      toast.error(t('myPages.statusError'));
    }
    setActionMenuId(null);
  };

  const handleDelete = async (entity) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = entity.type === 'kennel' 
        ? `${API}/kennels/${entity.id}`
        : `${API}/${entity.type}s/${entity.id}`;
      
      await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success(t('myPages.deleted'));
      setDeleteConfirmEntity(null);
      fetchEntities();
    } catch (err) {
      toast.error(t('myPages.deleteError'));
    }
  };

  const handleBooster = (entity) => {
    navigate('/panel/booster-v2-new');
    setActionMenuId(null);
  };

  const copyShareLink = (entity) => {
    const config = ENTITY_CONFIG[entity.type];
    if (config && entity.slug) {
      const url = `${window.location.origin}${config.viewRoute(entity.slug)}`;
      navigator.clipboard.writeText(url);
      toast.success(t('myPages.linkCopied'));
    }
    setShareModalEntity(null);
  };

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <PanelLayout>
        <div className={`min-h-screen ${THEME.bg} flex items-center justify-center`}>
          <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
        </div>
      </PanelLayout>
    );
  }

  // ==================== EMPTY STATE ====================
  if (entities.length === 0) {
    return (
      <PanelLayout>
        <div className={`min-h-screen ${THEME.bg} p-6`}>
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="w-20 h-20 mx-auto rounded-full bg-teal-100 flex items-center justify-center mb-6">
              <Home className="w-10 h-10 text-teal-500" />
            </div>
            <h2 className={`text-2xl font-semibold ${THEME.text} mb-3`}>
              {t('myPages.noPages')}
            </h2>
            <p className={`${THEME.text2} mb-8`}>
              {t('myPages.noPagesDesc')}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button
                onClick={() => navigate(routes.hodowlaCreate())}
                className={`${THEME.accent} hover:bg-teal-600 text-white rounded-xl px-6`}
              >
                <Home className="w-4 h-4 mr-2" />
                {t('myPages.createKennel')}
              </Button>
              <Button
                onClick={() => navigate(routes.miotCreate())}
                variant="outline"
                className="rounded-xl px-6"
              >
                <Baby className="w-4 h-4 mr-2" />
                {t('myPages.addLitter')}
              </Button>
            </div>
          </div>
        </div>
      </PanelLayout>
    );
  }

  // ==================== MAIN VIEW ====================
  return (
    <PanelLayout>
      <div className={`min-h-screen ${THEME.bg} p-6`}>
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className={`text-2xl font-semibold ${THEME.text}`}>{t('myPages.title')}</h1>
              <p className={THEME.text2}>{t('myPages.subtitle')}</p>
            </div>
            <Button
              onClick={() => navigate(routes.miotCreate())}
              className={`${THEME.accent} hover:bg-teal-600 text-white rounded-xl`}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('myPages.addOffer')}
            </Button>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {entities.map((entity) => {
              const config = ENTITY_CONFIG[entity.type];
              const status = STATUS_CONFIG[entity.status] || STATUS_CONFIG.draft;
              const Icon = config?.icon || Home;
              
              return (
                <motion.div
                  key={`${entity.type}-${entity.id}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`${THEME.surface} rounded-2xl border ${THEME.border} overflow-hidden hover:shadow-lg transition-shadow`}
                >
                  {/* Image */}
                  <div className="relative h-40 bg-zinc-100">
                    {entity.image ? (
                      <img 
                        src={entity.image} 
                        alt={entity.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon className="w-12 h-12 text-gray-300" />
                      </div>
                    )}
                    
                    {/* Status badge */}
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color} flex items-center gap-1`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                        {t(`myPages.status${entity.status.charAt(0).toUpperCase() + entity.status.slice(1)}`)}
                      </span>
                    </div>
                    
                    {/* Type badge */}
                    <span className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${config?.color || 'bg-zinc-100 text-zinc-600'}`}>
                      {config?.label || entity.type}
                    </span>
                    
                    {/* Booster badge */}
                    {entity.booster_active && (
                      <span className="absolute bottom-3 left-3 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700 flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        Booster ON
                      </span>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    <h3 className={`font-semibold ${THEME.text} mb-1 truncate`}>
                      {entity.name}
                    </h3>
                    <p className={`text-sm ${THEME.text2} mb-4`}>
                      {entity.breed || entity.description?.slice(0, 50) || ''}
                    </p>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleView(entity)}
                        className="rounded-xl flex-1"
                        disabled={!entity.slug}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {t('myPages.view')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleEdit(entity)}
                        className={`${THEME.accent} hover:bg-teal-600 text-white rounded-xl flex-1`}
                      >
                        <Edit3 className="w-4 h-4 mr-1" />
                        {t('myPages.edit')}
                      </Button>
                      
                      {/* More actions dropdown */}
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setActionMenuId(actionMenuId === entity.id ? null : entity.id)}
                          className="rounded-xl p-2"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                        
                        {actionMenuId === entity.id && (
                          <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-zinc-200 py-1 z-10">
                            <button
                              onClick={() => { setShareModalEntity(entity); setActionMenuId(null); }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-zinc-50 flex items-center gap-2"
                            >
                              <Share2 className="w-4 h-4" />
                              {t('myPages.share')}
                            </button>
                            <button
                              onClick={() => handleTogglePause(entity)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-zinc-50 flex items-center gap-2"
                            >
                              {entity.status === 'paused' ? (
                                <>
                                  <Play className="w-4 h-4" />
                                  {t('myPages.resume')}
                                </>
                              ) : (
                                <>
                                  <Pause className="w-4 h-4" />
                                  {t('myPages.pause')}
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => handleBooster(entity)}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-zinc-50 flex items-center gap-2 text-orange-600"
                            >
                              <Zap className="w-4 h-4" />
                              {t('myPages.promoteBooster')}
                            </button>
                            <hr className="my-1" />
                            <button
                              onClick={() => { setDeleteConfirmEntity(entity); setActionMenuId(null); }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t('common.delete')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {shareModalEntity && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShareModalEntity(null)}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${THEME.text}`}>{t('myPages.share')}</h3>
              <button onClick={() => setShareModalEntity(null)} className="p-1 rounded-lg hover:bg-zinc-100">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => copyShareLink(shareModalEntity)}
                className="w-full p-3 rounded-xl border border-zinc-200 hover:bg-zinc-50 flex items-center gap-3"
              >
                <ExternalLink className="w-5 h-5 text-zinc-500" />
                <span className={THEME.text}>{t('myPages.copyLink')}</span>
              </button>
              <button
                onClick={() => {
                  const config = ENTITY_CONFIG[shareModalEntity.type];
                  if (config && shareModalEntity.slug) {
                    const url = `${window.location.origin}${config.viewRoute(shareModalEntity.slug)}`;
                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                  }
                  setShareModalEntity(null);
                }}
                className="w-full p-3 rounded-xl border border-zinc-200 hover:bg-zinc-50 flex items-center gap-3"
              >
                <Share2 className="w-5 h-5 text-blue-500" />
                <span className={THEME.text}>{t('myPages.shareOnFacebook')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmEntity && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setDeleteConfirmEntity(null)}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            <h3 className={`text-lg font-semibold ${THEME.text} text-center mb-2`}>
              {t('myPages.deleteConfirm', { name: deleteConfirmEntity.name })}
            </h3>
            <p className={`${THEME.text2} text-center mb-6`}>
              {t('myPages.deleteDesc')}
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmEntity(null)}
                className="flex-1 rounded-xl"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => handleDelete(deleteConfirmEntity)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl"
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close action menu */}
      {actionMenuId && (
        <div 
          className="fixed inset-0 z-[5]" 
          onClick={() => setActionMenuId(null)}
        />
      )}
    </PanelLayout>
  );
}
